import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import { Box, Flex } from 'rebass/styled-components';
import { usePopperTooltip } from 'react-popper-tooltip';
import { FormLabel, Input } from '@getro/rombo';
import { ChevronDown, X } from 'lucide-react';
import moment from 'moment';
import DatePicker, { FORMAT } from '../../../../components/atoms/DatePicker';
import { FormikAutoSubmit } from '../../../../components/atoms/formikAutoSubmit';

export const MovementDateFilter = ({ dateFrom, dateTo, setFilters }) => {
  const [controlledVisible, setControlledVisible] = useState(false);
  const [isPreset, setIsPreset] = useState(false);
  const { getTooltipProps, setTooltipRef, setTriggerRef, visible } = usePopperTooltip({
    trigger: 'click',
    interactive: true,
    placement: 'bottom-start',
    visible: controlledVisible,
    onVisibleChange: (newVisible) => {
      if (newVisible && dateFrom) {
        setPickerKey((prev) => prev + 1);
      }
      setControlledVisible(newVisible);
    },
  });

  const dates = useMemo(() => {
    if (dateFrom && dateTo) {
      return {
        startDate: moment(dateFrom),
        endDate: moment(dateTo),
      };
    }
    return { startDate: null, endDate: null };
  }, [dateFrom, dateTo]);

  const [pickerKey, setPickerKey] = useState(0);

  const displayText = useMemo(() => {
    if (!dateFrom || !dateTo) return '';
    return `${moment(dateFrom).format('MM/DD/YYYY')} - ${moment(dateTo).format('MM/DD/YYYY')}`;
  }, [dateFrom, dateTo]);

  const handleDateChange = (selectedDates, options = {}) => {
    if (!selectedDates.startDate || !selectedDates.endDate) return;

    if (options.isPreset) {
      setIsPreset(true);
      setControlledVisible(false);
    } else if (isPreset) {
      setIsPreset(false);
    }

    setFilters({
      dateFrom: selectedDates.startDate.format('YYYY-MM-DD'),
      dateTo: selectedDates.endDate.format('YYYY-MM-DD'),
    });

    const isSameEndDate = dates.endDate && selectedDates.endDate.isSame(dates.endDate, 'day');

    if (!options.isPreset && selectedDates.startDate && selectedDates.endDate && !isSameEndDate) {
      setTimeout(() => {
        setControlledVisible(false);
      }, 100);
    }
  };

  const handleClear = () => {
    setFilters({ dateFrom: '', dateTo: '' });
    setControlledVisible(false);
  };

  return (
    <Formik initialValues={{ dateFrom, dateTo }} enableReinitialize>
      <Form>
        <FormikAutoSubmit />
        <Box>
          <FormLabel>Date range</FormLabel>
          <Flex
            ref={setTriggerRef}
            sx={{
              input: { m: 0, borderColor: 'border.default' },
              position: 'relative',
              alignItems: 'center',
              svg: { color: 'neutral.400' },
              cursor: 'pointer',
            }}
          >
            <Input
              autoComplete="off"
              name="movementDate"
              placeholder="MM/DD/YYYY - MM/DD/YYYY"
              onChange={(e) => e.preventDefault()}
              size="small"
              mt="3"
              sx={{
                maxHeight: '32px',
                height: '32px',
                outline: 'none',
                cursor: 'pointer',
              }}
              value={displayText}
              readOnly
            />
            {displayText && (
              <Box
                strokeWidth="1.5"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                sx={{ position: 'absolute', right: '32px', cursor: 'pointer', zIndex: 1 }}
                as={X}
                size="14px"
              />
            )}
            <Box
              strokeWidth="1.5"
              sx={{ position: 'absolute', right: '8px', cursor: 'pointer', pointerEvents: 'none' }}
              as={ChevronDown}
              size="16px"
            />
          </Flex>
          {visible &&
            createPortal(
              <Box
                ref={setTooltipRef}
                {...getTooltipProps({ className: 'dropdown-container' })}
                sx={{
                  zIndex: 3333333333,
                  boxShadow: '0px 1px 8px rgba(27, 36, 50, 0.1), 0px 4px 16px rgba(27, 36, 50, 0.1)',
                  position: 'relative',
                  cursor: 'pointer',
                  '.DayPicker': {
                    width: '100%!important',
                    zIndex: 2,
                    left: '0!important',
                    ml: ['0!important'],
                    mt: '0!important',
                    position: ['relative!important'],
                    margin: '0px auto',
                    border: 'none',
                    boxShadow: 'none',
                    '& > div > div': {
                      margin: '0px auto',
                    },
                  },
                  '& > svg': {
                    color: 'neutral.500',
                  },
                }}
              >
                <DatePicker
                  key={pickerKey}
                  useRangeController
                  onSelectDates={handleDateChange}
                  onOutsideClick={() => {
                    setTimeout(() => {
                      setControlledVisible(false);
                    }, 100);
                  }}
                  formatDate={false}
                  startDate={dates.startDate ? dates.startDate.format(FORMAT) : ''}
                  endDate={dates.endDate ? dates.endDate.format(FORMAT) : ''}
                />
              </Box>,
              document.body,
            )}
        </Box>
      </Form>
    </Formik>
  );
};

MovementDateFilter.propTypes = {
  dateFrom: PropTypes.string,
  dateTo: PropTypes.string,
  setFilters: PropTypes.func.isRequired,
};

MovementDateFilter.defaultProps = {
  dateFrom: '',
  dateTo: '',
};
